import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Search, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { OrgPortalApi } from '@/services/org.api';
import { OrgReportsApi, OrgTeachersReportRow } from '@/services/org-reports.api';
import type { ExportFormat } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input, Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { formatMoney } from '@/lib/format';

const columnHelper = createColumnHelper<OrgTeachersReportRow>();

/**
 * Teachers Report — org tier, read-only. Cross-school staff roster
 * scoped to role=teacher, matching the Finance/Login report pattern
 * (a School filter). Paginated via the shared DataTable (same convention
 * as StudentsReportPage.tsx) rather than a flat unpaginated table.
 */
export function OrgTeachersReportPage() {
  const [schoolId, setSchoolId] = useState('');
  const [showDeactive, setShowDeactive] = useState(false);
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const dt = useDataTable({ defaultLimit: 20 });

  useEffect(() => {
    dt.onPaginationChange((prev) => ({ ...prev, pageIndex: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, showDeactive]);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-org-teachers-report'],
    queryFn: async () => (await OrgPortalApi.listSchools({ limit: 100 })).items,
  });

  const filterParams = { schoolId: schoolId || undefined, showDeactive: showDeactive || undefined };

  const { data, isFetching } = useQuery({
    queryKey: ['org-teachers-report', dt.params, filterParams],
    queryFn: () => OrgReportsApi.teachersReport({ ...dt.params, ...filterParams }),
    placeholderData: keepPreviousData,
  });

  const runExport = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(format);
    try {
      await OrgReportsApi.exportTeachersReport(format, { ...filterParams, search: dt.params.search });
    } finally {
      setBusy(null);
    }
  };

  const columns = [
    columnHelper.display({
      id: 'no',
      header: 'No.',
      enableSorting: false,
      cell: (info) => (
        <span className="text-slate-400">{dt.pagination.pageIndex * dt.pagination.pageSize + info.row.index + 1}</span>
      ),
    }),
    columnHelper.accessor('schoolName', { header: 'School' }),
    columnHelper.accessor('staffName', { header: 'Name' }),
    columnHelper.accessor('gender', { header: 'Gender', cell: (ctx) => ctx.getValue() ?? '—' }),
    columnHelper.accessor('email', { header: 'Email', cell: (ctx) => ctx.getValue() ?? '—' }),
    columnHelper.accessor('designation', { header: 'Designation', cell: (ctx) => ctx.getValue() ?? '—' }),
    columnHelper.accessor('department', { header: 'Department', cell: (ctx) => ctx.getValue() ?? '—' }),
    columnHelper.accessor('salary', { header: 'Salary', cell: (ctx) => formatMoney(ctx.getValue()) }),
    columnHelper.accessor('address', { header: 'Address', cell: (ctx) => ctx.getValue() ?? '—' }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: (ctx) => <Badge tone={ctx.getValue() === 'active' ? 'green' : 'slate'}>{ctx.getValue()}</Badge>,
    }),
  ];

  return (
    <>
      <PageHeader
        title="Teachers Report"
        description="Teaching staff — name, gender, contact, salary, and address, across your organization's schools."
        actions={
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('xlsx')}
              disabled={!!busy}
              title="Export to Excel"
            >
              {busy === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
              Excel
            </button>
            <div className="w-px bg-slate-200" />
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('pdf')}
              disabled={!!busy}
              title="Export to PDF"
            >
              {busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-600" />}
              PDF
            </button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={dt.searchInput}
            onChange={(e) => dt.setSearchInput(e.target.value)}
            placeholder="Search by name"
            className="w-64 pl-9"
          />
        </div>
        <Select className="!w-56" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
          <option value="">All schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <label className="flex items-center gap-1.5 text-sm text-slate-600">
          <input type="checkbox" checked={showDeactive} onChange={(e) => setShowDeactive(e.target.checked)} />
          Show Deactive
        </label>
      </div>

      <DataTable
        columns={columns}
        result={data}
        state={dt}
        loading={isFetching}
        emptyMessage="No teachers found."
        getRowId={(row) => `${row.schoolId}:${row.employeeId}`}
      />
    </>
  );
}
